import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, Clock, Briefcase, ArrowLeft } from "lucide-react";
import { SecondaryButton } from "../../StyledCss/styledbtn";
import toast from "react-hot-toast";
import SlidingModal from "../SlidingModal/slidingModal";
import {
  StyledLabel,
  StyledInputGroup,
  StyledInputContainer,
  StyledInput,
} from "../../StyledCss/styledInputs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton } from "../../StyledCss/styledbtn";
import { StatCard } from "./StatCard";
import { MonthlyCard } from "./MonthlyCard";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { convertKeysToSnakeCase } from "../../util/convertToSnakeCase";
import {
  getMonthlyWorkLogs,
  getUserData,
  exportWorklogs,
  getProjectsByEmpId,
  addWorkLog,
  exportWorklogsZip
  
} from "../../api/services/hroneApi";
import "./index.css";
import AddWorkLogPopup from "../AddWorkLogPopUp";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const WorkLogsDashBoard = ({ path }) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(FEATURE_CODES.WORKLOG_CREATE);
  const canExport = hasPermission(FEATURE_CODES.WORKLOG_EXPORT);
  const canViewAll = hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL);
  const { empId, role } = getUserData();
  const { project_id, employee_id } = useParams();
  const isAdminViewingEmployeeWorklogs = path === "worklogs_dashboard" && canViewAll;
  const [selectedProject, setSelectedProject] = useState("");
  const [projectsData, setProjectsData] = useState([]);
  const [selectYear, setSelectYear] = useState(
    new Date().getFullYear().toString(),
  );

  const navigate = useNavigate();
  const [monthlyWorksLogs, setMonthlyWorksLogs] = useState([]);
  const [yearlyWorksLogs, setYearlyWorksLogs] = useState({});
  const [yearsList, setYearsList] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState({
      addWorklog: "",
    });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [name, setName] = useState("");
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const dropdownRef = useRef(null);
  const [loadingStates, setLoadingStates] = useState({
    exportMonth: false,
    exportCustom: false,
    addWorklog: false,
  });
  const setLoading = (operation, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterDropdownOpen]);
  const toggleFilterDropdown = () => {
    setFromDate("");
    setToDate("");
    setIsFilterDropdownOpen((prev) => !prev);
  };
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 4;
    const years = Array.from(
      { length: currentYear - startYear + 1 },
      (_, index) => {
        const yearValue = startYear + index;
        return { label: yearValue.toString(), value: yearValue.toString() };
      },
    );

    setYearsList(years.reverse());
  }, []);
  const getProjectsList = async () => {
    try {
      const response = await getProjectsByEmpId();

      if (response.ok) {
        const data = await response.json();
        setProjectsData(data.map(convertKeysToCamelCase));
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };
  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);
    fetchMonthlyWorkLogs(selectYear, projectId);
  };
  const handleThisMonth = async () => {
    const projectId = project_id || selectedProject;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0);
    const endDate = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;

    try {
      setLoading("exportMonth", true);

      let response;
      if (!projectId) {
        const projectIds = projectsData
          .flatMap(project => project.projectId || [])
          .join(',');
        response = await exportWorklogsZip(projectIds, startDate, endDate);
      } else {
        response = await exportWorklogs(projectId, isAdminViewingEmployeeWorklogs ? employee_id : null, startDate, endDate);
      }

      if (response.ok) {
       const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers.get("content-disposition");
        let filename;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match) {
            filename = match[1];
          }
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Worklogs exported successfully");
        toggleFilterDropdown();
      } else {
        toast.error("Failed to export worklogs");
        toggleFilterDropdown();
      }
    } catch (error) {
      toast.error("Failed to export worklogs. Please try again.");
      toggleFilterDropdown();
    } finally {
      setLoading("exportMonth", false);
    }
    setIsFilterDropdownOpen(false);
  };
  const resetForm = () => {
    setIsPopupOpen(false);
    setErrorMessages({ addWorklog: "" });
  };
  const handleDateSubmit = async () => {
    if (!fromDate || !toDate) { 
      toast.error("Please select both From and To dates.");
      return;
    }
    if (fromDate > toDate) {
      toast.error("From date cannot be after To date.");
      return;
    }

    try {
      setLoading("exportCustom", true);
      const projectId = project_id || selectedProject;

      let response;
      if (!projectId) {
        const projectsIds = projectsData
          .flatMap(project => project.projectId || [])
          .join(',');
        response = await exportWorklogsZip(projectsIds, fromDate, toDate);
      } else {
        response = await exportWorklogs(projectId, isAdminViewingEmployeeWorklogs ? employee_id : null, fromDate, toDate);
      }

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers.get("content-disposition");
        let filename = "download.zip";
        if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) {
          filename = match[1]; 
        }
      }
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Worklogs exported successfully");
        toggleFilterDropdown();
      } else {
        toast.error("Failed to export worklogs");
        toggleFilterDropdown();
      }
    } catch (error) {
      toast.error("Failed to export worklogs. Please try again.");
      toggleFilterDropdown();
    } finally {
      setLoading("exportCustom", false);
    }
    setIsFilterDropdownOpen(false);
  };


  const fetchMonthlyWorkLogs = async (year = selectYear, projectId = selectedProject) => {
      setApiStatus(apiStatusConstants.inProgress);
      try {
        const response = await getMonthlyWorkLogs(
          year || new Date().getFullYear(),
          isAdminViewingEmployeeWorklogs ? employee_id : empId,
          isAdminViewingEmployeeWorklogs ? project_id : projectId,
        );

        if (response.ok) {
          const data = convertKeysToCamelCase(await response.json());
          setName(data.empName);
          setYearlyWorksLogs(data.yearlySummary);
          setMonthlyWorksLogs(data.monthlyData);
          setApiStatus(apiStatusConstants.success);
        } else {
          setApiStatus(apiStatusConstants.failure);
          toast.error("Failed to fetch worklogs");
        }
      } catch (error) {
        console.error("Error fetching worklogs:", error);
        setApiStatus(apiStatusConstants.failure);
        toast.error("Failed to fetch worklogs.");
      }
  };

  const handleWorkLogSubmit = async (data) => {
        const workLogData = convertKeysToSnakeCase({
          ...data,
          employeeId: empId,
        });
  
        try {
          setLoading("addWorklog", true);
          const response = await addWorkLog(workLogData);
  
          if (response.ok) {
            toast.success(
               "Work log added successfully",
            );
            fetchMonthlyWorkLogs()
            resetForm();
          } else {
            const errorData = await response.json();
          setErrorMessages({addWorklog:errorData.detail});
            
          }
        } catch (error) {
          console.error(`Error:`, error);
          toast.error("An error occurred. Please try again.");
        } 
        finally {
          setLoading("addWorklog", false);
        }

  };
  
  const onClickRetry = () => {
    fetchMonthlyWorkLogs();
  };
  useEffect(() => {
    fetchMonthlyWorkLogs();
  }, [isAdminViewingEmployeeWorklogs]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdminViewingEmployeeWorklogs) {
      getProjectsList();
    }
  }, [isAdminViewingEmployeeWorklogs]);  // eslint-disable-line react-hooks/exhaustive-deps
  const statsData = [
    {
      title: "Total Logged Hours",
      value: yearlyWorksLogs.loggedHours,
      icon: Clock,
      accentColor: "#2563EB",
    },
    {
      title: "Total Yearly Working Days",
      value: yearlyWorksLogs.totalWorkingDays,
      icon: Users,
      accentColor: "#059669",
    },
    {
      title: "Total Projects",
      value: yearlyWorksLogs.totalProjects,
      icon: Briefcase,
      accentColor: "#4F46E5",
    },
  ];

  const handleCardClick = (year, month) => {
    if (!canViewAll) return;
    if (isAdminViewingEmployeeWorklogs) {
      navigate(`/worklogs/${project_id}/${employee_id}/${year}/${month}`, {
        state: { isAdminViewingEmployeeWorklogs: true },
      });
    } else {
      const path = selectedProject
        ? `/worklogs/${selectedProject}/${year}/${month}`
        : `/worklogs/${year}/${month}`;
      navigate(path);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectYear(year);
    if (year) {
      fetchMonthlyWorkLogs(year, selectedProject);
    }
  };

  const handleFromDateChange = (e) => {
    const minDate = selectedProject ? projectsData.find(p => p.projectId === selectedProject)?.startDate : "";
    if (minDate && e.target.value < minDate) {
      return;
    }
    setFromDate(e.target.value);
  };
    
  const handleToDateChange = (e) => {
    const minDate = fromDate || (selectedProject ? projectsData.find(p => p.projectId === selectedProject)?.startDate : "");
    if (minDate && e.target.value < minDate) {
      return;
    }
    setToDate(e.target.value);
  };

  const openAddWorkLogPopup = () => {
    setIsPopupOpen(true);
  };
    

  const renderContent = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;

      case apiStatusConstants.success:
        return (
          <>
            <div className="row g-3 px-3 mx-0">
              {statsData.map((item, index) => (
                <div
                  key={index}
                  className="col-6 col-md-3 col-lg-3 col-xl-2 d-flex"
                >
                  <StatCard {...item} />
                </div>
              ))}
            </div>

            {monthlyWorksLogs.length > 0 && (
              <div
                className="card shadow-sm p-3 m-4 chart-container"
                style={{ borderRadius: "12px" }}
              >
                <h4 className="fw-semibold mb-3">Hours Overview</h4>

                <div style={{ height: "280px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyWorksLogs}>
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          color: "#000",
                        }}
                      />
                      <Bar
                        dataKey="loggedHours"
                        fill="#0d6efd"
                        radius={[5, 5, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <h4 className="fw-semibold m-3">Monthly Overview</h4>

            <div className="row g-3 px-3 pb-4 mx-0">
              {monthlyWorksLogs &&
                Array.isArray(monthlyWorksLogs) &&
                monthlyWorksLogs.map((item, index) => {
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear();
                  const currentMonth = currentDate.getMonth() + 1;
                  const isDisabled = item.year > currentYear || (item.year === currentYear && item.monthNum > currentMonth) || !canViewAll;

                  return (
                    <div
                      key={index}
                      className="col-6 col-md-4 col-lg-3 col-xl-2 d-flex"
                    >
                      <MonthlyCard
                        stats={item}
                        onClick={() => handleCardClick(item.year, item.monthNum)}
                        disabled={isDisabled}
                      />
                    </div>
                  );
                })}
            </div>
          </>
        );
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={onClickRetry} />;
      default:
        return null;
    }
  };

  return (
    <div>

      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName >
              {isAdminViewingEmployeeWorklogs ? (
                <>
                  {["Admin", "Lead"].includes(role) && <ArrowLeft size={20}   
                  onClick={handleBackClick}
                  style={{ cursor: "pointer" }} />} {name} - WorkLogs
                </>
              ) : (
                <>
                  {["Admin", "Lead"].includes(role) && <ArrowLeft size={20}   
                  onClick={handleBackClick}
                  style={{ cursor: "pointer" }} />} WorkLogs Dashboard
                </>
              )}
            </PageHeaderName>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              {!isAdminViewingEmployeeWorklogs && canCreate && (() => {
                const selectedProjectData = projectsData.find(p => p.projectId === selectedProject);
                const isAddDisabled = projectsData.length === 0 || (selectedProject !== "" && selectedProjectData && !selectedProjectData.active);
                return (
                  <div title={projectsData.length === 0 ? "No projects assigned yet" : isAddDisabled ? "Project is inactive" : ""}>
                    <PrimaryButton
                      onClick={openAddWorkLogPopup}
                      disabled={isAddDisabled}
                    >
                      Add WorkLog
                    </PrimaryButton>
                  </div>
                );
              })()}
              {!isAdminViewingEmployeeWorklogs && (
                <select
                  className="selectElement"
                  value={selectedProject}
                  onChange={handleProjectChange}
                >
                  <option value="">All Projects</option> 

                 {projectsData.length === 0 ? (
                      <option value="" disabled>
                        No projects available
                      </option>
                    ) : (
                      projectsData.map((project) => (
                        <option
                          key={project.projectId}
                          value={project.projectId}
                        >
                          {project.projectName}
                        </option>
                      ))
                    )}
                </select>
              )}
              <select className="selectElement" value={selectYear} onChange={handleYearChange}>
                <option value="">Select Year</option>
                {yearsList.map((eachYear) => (
                  <option key={eachYear.value} value={eachYear.value}>
                    {eachYear.label}
                  </option>
                ))}
              </select>
              {canExport && (
              <div
                className="dropdown"
                style={{ position: "relative" }}
                ref={dropdownRef}
                title={!project_id && projectsData.length === 0 ? "No projects assigned yet" : ""}
              >
                <PrimaryButton onClick={toggleFilterDropdown} disabled={!project_id && projectsData.length === 0}>
                  Export
                </PrimaryButton>
                {isFilterDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "12px",
                      width: "450px",
                      marginTop: "20px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <PrimaryButton
                      onClick={handleThisMonth}
                      disabled={loadingStates.exportMonth}
                    >
                      {loadingStates.exportMonth
                        ? "Exporting..."
                        : "This Month"}
                    </PrimaryButton>

                    <hr />
                    <h4
                      style={{
                        margin: "0 0 15px 0",
                        color: "#333",
                        fontSize: "16px",
                        fontWeight: "600",
                      }}
                    >
                      Custom Date Range
                    </h4>

                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        alignItems: "flex-start",
                        marginTop: "25px",
                        marginBottom: "15px",
                      }}
                    >
                      <StyledInputGroup style={{ flex: 1 }}>
                        <StyledInputContainer>
                          <StyledInput
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            min={selectedProject ? projectsData.find(p => p.projectId === selectedProject)?.startDate : ""}
                            style={{ width: "200px" }}
                            max="9999-12-31"

                          />
                          <StyledLabel hasValue className="user-label">
                            From Date*
                          </StyledLabel>
                        </StyledInputContainer>
                      </StyledInputGroup>

                      <StyledInputGroup style={{ flex: 1 }}>
                        <StyledInputContainer>
                          <StyledInput
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            min={fromDate || (selectedProject ? projectsData.find(p => p.projectId === selectedProject)?.startDate : "")}
                            style={{ width: "200px" }}
                            max="9999-12-31"
                          />
                          <StyledLabel hasValue className="user-label">
                            To Date*
                          </StyledLabel>
                        </StyledInputContainer>
                      </StyledInputGroup>
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <SecondaryButton onClick={toggleFilterDropdown}>
                        Cancel
                      </SecondaryButton>

                      <PrimaryButton
                        onClick={handleDateSubmit}
                        disabled={loadingStates.exportCustom}
                      >
                        {loadingStates.exportCustom ? "Exporting..." : "Submit"}
                      </PrimaryButton>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </PageHeader>
 
          {renderContent()}
        </div>
      </div>
      <SlidingModal
              modalName={"Add WorkLog"}
              closeBtn={resetForm}
              isModalOpen={isPopupOpen}
              marginBottom="0px"
            >
              <AddWorkLogPopup
                onSubmitData={handleWorkLogSubmit}
                closeBtn={resetForm}
                selectedProjectId={selectedProject}
                errorMsg={errorMessages.addWorklog}
                isLoading={loadingStates.addWorklog}
                onClearError={() => setErrorMessages(prev => ({ ...prev, addWorklog: "" }))}
                projectsData={projectsData.filter(p => p.active)}
              />
            </SlidingModal>
    </div>
  );
};

export default WorkLogsDashBoard;
